import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  X, Check, Calendar, Users, Calculator, Loader2,
  Save, DollarSign, AlertCircle, MessageSquare, Filter,
  ChevronDown, Edit2, Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';

// Common absence reasons for quick selection
const COMMON_REASONS = [
  'Trabalho',
  'Compromisso pessoal',
  'Chegando mais tarde',
  'Saindo mais cedo',
  'Só vou no final de semana',
  'Problema de transporte',
];

// Absence Reason Modal
const AbsenceReasonModal = ({ isOpen, onClose, onConfirm, memberName, day }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const handleConfirm = () => {
    const finalReason = reason === 'custom' ? customReason : reason;
    onConfirm(finalReason);
    setReason('');
    setCustomReason('');
  };

  const handleCancel = () => {
    setReason('');
    setCustomReason('');
    onClose();
  };

  const formattedDay = day ? format(parseISO(day), "dd 'de' MMMM", { locale: ptBR }) : '';

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-orange-500" />
            Motivo da Ausência
          </DialogTitle>
          <DialogDescription>
            Por que <strong>{memberName}</strong> não vai participar no dia <strong>{formattedDay}</strong>?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex flex-wrap gap-2">
            {COMMON_REASONS.map((r) => (
              <button
                key={r}
                onClick={() => setReason(r)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  reason === r
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {r}
              </button>
            ))}
            <button
              onClick={() => setReason('custom')}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                reason === 'custom'
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              Outro motivo...
            </button>
          </div>

          {reason === 'custom' && (
            <div>
              <Label htmlFor="custom-reason">Descreva o motivo</Label>
              <Textarea
                id="custom-reason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Ex: Tenho uma reunião importante..."
                className="mt-1"
                rows={2}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button variant="outline" onClick={() => onConfirm('')}>
            Sem motivo
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!reason || (reason === 'custom' && !customReason.trim())}
            className="bg-orange-500 hover:bg-orange-600"
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Day Filter Component
const DayFilter = ({ days, selectedDay, onSelectDay, dailyAttendance }) => {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1 text-sm text-slate-500">
        <Filter className="w-4 h-4" />
        <span>Filtrar por dia:</span>
      </div>
      <button
        onClick={() => onSelectDay(null)}
        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
          !selectedDay
            ? 'bg-orange-500 text-white'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
        }`}
      >
        Todos os dias
      </button>
      {days.map(day => {
        const count = dailyAttendance[day]?.count || 0;
        const date = parseISO(day);
        return (
          <button
            key={day}
            onClick={() => onSelectDay(day)}
            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
              selectedDay === day
                ? 'bg-orange-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {format(date, 'dd/MM', { locale: ptBR })}
            <span className="ml-1 opacity-70">({count})</span>
          </button>
        );
      })}
    </div>
  );
};

// Day Attendees List
const DayAttendeesList = ({ day, members, localAttendance, absenceReasons }) => {
  const date = parseISO(day);
  const attending = members.filter(m => (localAttendance[m.user_id] || []).includes(day));
  const notAttending = members.filter(m => !(localAttendance[m.user_id] || []).includes(day));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-orange-500" />
          {format(date, "EEEE, dd 'de' MMMM", { locale: ptBR })}
        </CardTitle>
        <CardDescription>
          {attending.length} pessoa(s) confirmada(s)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Attending */}
        <div>
          <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-2 flex items-center gap-1">
            <Check className="w-4 h-4" />
            Vão participar ({attending.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {attending.map(member => (
              <div
                key={member.user_id}
                className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg"
              >
                <span className="text-lg">{member.avatar}</span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {member.name}
                </span>
              </div>
            ))}
            {attending.length === 0 && (
              <p className="text-sm text-slate-400">Ninguém confirmado ainda</p>
            )}
          </div>
        </div>

        {/* Not Attending */}
        {notAttending.length > 0 && (
          <div>
            <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-2 flex items-center gap-1">
              <X className="w-4 h-4" />
              Não vão participar ({notAttending.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {notAttending.map(member => {
                const reason = absenceReasons[`${member.user_id}-${day}`];
                return (
                  <TooltipProvider key={member.user_id}>
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <span className="text-lg">{member.avatar}</span>
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {member.name}
                          </span>
                          {reason && <MessageSquare className="w-3 h-3 text-slate-400" />}
                        </div>
                      </TooltipTrigger>
                      {reason && (
                        <TooltipContent>
                          <p>Motivo: {reason}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Attendance Grid Component
export const AttendanceGrid = ({ tripId, isOrganizer = false, currentUserId = null, compact = false }) => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null); // Which user is being edited
  const [localAttendance, setLocalAttendance] = useState({});
  const [absenceReasons, setAbsenceReasons] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null); // For day filter
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'day'
  
  const [reasonModal, setReasonModal] = useState({ 
    isOpen: false, 
    userId: null, 
    day: null, 
    memberName: '' 
  });

  useEffect(() => {
    fetchAttendance();
  }, [tripId]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips/${tripId}/attendance`);
      setData(response.data);
      
      const initial = {};
      const reasons = {};
      response.data.members.forEach(m => {
        initial[m.user_id] = m.attendance_days || response.data.days;
        if (m.absence_reasons) {
          Object.entries(m.absence_reasons).forEach(([day, reason]) => {
            reasons[`${m.user_id}-${day}`] = reason;
          });
        }
      });
      setLocalAttendance(initial);
      setAbsenceReasons(reasons);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Erro ao carregar dados de presença');
    } finally {
      setLoading(false);
    }
  };

  const canEditMember = (memberId) => {
    if (isOrganizer) return true;
    return memberId === user?.id;
  };

  const startEditingSelf = () => {
    setEditMode(true);
    setEditingUserId(user?.id);
  };

  const startEditingAll = () => {
    setEditMode(true);
    setEditingUserId(null); // null means editing all (organizer mode)
  };

  const handleDayClick = (userId, day, memberName) => {
    if (!editMode) return;
    
    // Check if user can edit this member
    if (editingUserId && editingUserId !== userId) return;
    if (!isOrganizer && userId !== user?.id) return;
    
    const current = localAttendance[userId] || [];
    const isAttending = current.includes(day);
    
    if (isAttending) {
      setReasonModal({
        isOpen: true,
        userId,
        day,
        memberName
      });
    } else {
      setLocalAttendance(prev => ({
        ...prev,
        [userId]: [...(prev[userId] || []), day].sort()
      }));
      setAbsenceReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons[`${userId}-${day}`];
        return newReasons;
      });
    }
  };

  const confirmAbsence = (reason) => {
    const { userId, day } = reasonModal;
    
    setLocalAttendance(prev => ({
      ...prev,
      [userId]: (prev[userId] || []).filter(d => d !== day)
    }));
    
    if (reason) {
      setAbsenceReasons(prev => ({
        ...prev,
        [`${userId}-${day}`]: reason
      }));
    }
    
    setReasonModal({ isOpen: false, userId: null, day: null, memberName: '' });
  };

  const saveAttendance = async () => {
    try {
      setSaving(true);
      
      if (editingUserId) {
        // Save only for current user (participant mode)
        const userReasons = {};
        data.days.forEach(day => {
          if (!(localAttendance[editingUserId] || []).includes(day)) {
            const reason = absenceReasons[`${editingUserId}-${day}`];
            if (reason) {
              userReasons[day] = reason;
            }
          }
        });
        
        await api.put(`/trips/${tripId}/members/${editingUserId}/attendance`, {
          attendance_days: localAttendance[editingUserId] || [],
          absence_reasons: userReasons
        });
        
        toast.success('Sua presença foi atualizada!');
      } else {
        // Save all (organizer mode)
        await api.put(`/trips/${tripId}/attendance/bulk`, {
          attendance: localAttendance,
          absence_reasons: absenceReasons
        });
        
        toast.success('Presença atualizada com sucesso!');
      }
      
      setEditMode(false);
      setEditingUserId(null);
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar presença');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (data) {
      const initial = {};
      const reasons = {};
      data.members.forEach(m => {
        initial[m.user_id] = m.attendance_days || data.days;
        if (m.absence_reasons) {
          Object.entries(m.absence_reasons).forEach(([day, reason]) => {
            reasons[`${m.user_id}-${day}`] = reason;
          });
        }
      });
      setLocalAttendance(initial);
      setAbsenceReasons(reasons);
    }
    setEditMode(false);
    setEditingUserId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!data || data.days.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">
            Defina as datas da viagem para gerenciar a presença por dia
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDayHeader = (dateStr) => {
    const date = parseISO(dateStr);
    return format(date, "dd MMM", { locale: ptBR });
  };

  const formatDayOfWeek = (dateStr) => {
    const date = parseISO(dateStr);
    return format(date, 'EEE', { locale: ptBR });
  };

  // Check if current user is a member
  const currentUserMember = data.members.find(m => m.user_id === user?.id);
  const canEditSelf = currentUserMember && !isOrganizer;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Presença por Dia
            </CardTitle>
            <CardDescription>
              {data.members.length} membro(s) • {data.total_days} dia(s)
            </CardDescription>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-sm ${
                  viewMode === 'table' 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }`}
              >
                Tabela
              </button>
              <button
                onClick={() => setViewMode('day')}
                className={`px-3 py-1.5 text-sm ${
                  viewMode === 'day' 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }`}
              >
                Por Dia
              </button>
            </div>

            {editMode ? (
              <>
                <Button variant="outline" size="sm" onClick={cancelEdit}>
                  Cancelar
                </Button>
                <Button 
                  size="sm" 
                  onClick={saveAttendance}
                  disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar
                </Button>
              </>
            ) : (
              <>
                {canEditSelf && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={startEditingSelf}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Editar Minha Presença
                  </Button>
                )}
                {isOrganizer && (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={startEditingAll}
                    >
                      Editar Todos
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowPaymentModal(true)}
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Criar Pagamento
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Edit Mode Indicator */}
        {editMode && (
          <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-orange-500" />
            <span className="text-sm text-orange-700 dark:text-orange-400">
              {editingUserId 
                ? 'Editando sua presença - clique nos dias para alterar'
                : 'Editando presença de todos - clique nos dias para alterar'}
            </span>
          </div>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Day Filter */}
        {viewMode === 'day' && (
          <DayFilter 
            days={data.days} 
            selectedDay={selectedDay} 
            onSelectDay={setSelectedDay}
            dailyAttendance={data.daily_attendance}
          />
        )}

        {/* Day View */}
        {viewMode === 'day' && (
          <div className="space-y-4">
            {selectedDay ? (
              <DayAttendeesList 
                day={selectedDay}
                members={data.members}
                localAttendance={localAttendance}
                absenceReasons={absenceReasons}
              />
            ) : (
              data.days.map(day => (
                <DayAttendeesList 
                  key={day}
                  day={day}
                  members={data.members}
                  localAttendance={localAttendance}
                  absenceReasons={absenceReasons}
                />
              ))
            )}
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white dark:bg-slate-900 z-10 min-w-[150px]">
                      Membro
                    </TableHead>
                    {data.days.map(day => (
                      <TableHead key={day} className="text-center min-w-[60px]">
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-slate-400">{formatDayOfWeek(day)}</span>
                          <span>{formatDayHeader(day)}</span>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="text-center min-w-[80px]">
                      Total
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.members.map(member => {
                    const memberDays = localAttendance[member.user_id] || [];
                    const totalDays = data.days.filter(d => memberDays.includes(d)).length;
                    const isCurrentUser = member.user_id === user?.id;
                    const canEdit = editMode && (editingUserId === null || editingUserId === member.user_id);
                    
                    return (
                      <TableRow key={member.user_id} className={isCurrentUser ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''}>
                        <TableCell className="sticky left-0 bg-white dark:bg-slate-900 z-10">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{member.avatar}</span>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white text-sm">
                                {member.name}
                                {isCurrentUser && (
                                  <span className="ml-1 text-xs text-orange-500">(você)</span>
                                )}
                              </p>
                              {member.role === 'organizer' && (
                                <Badge variant="outline" className="text-xs">
                                  Organizador
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        
                        {data.days.map(day => {
                          const isAttending = memberDays.includes(day);
                          const reason = absenceReasons[`${member.user_id}-${day}`];
                          
                          return (
                            <TableCell 
                              key={day} 
                              className={`text-center ${canEdit ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800' : ''}`}
                              onClick={() => canEdit && handleDayClick(member.user_id, day, member.name)}
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    {isAttending ? (
                                      <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30">
                                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                      </div>
                                    ) : (
                                      <div className="inline-flex flex-col items-center justify-center">
                                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30">
                                          <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                                        </div>
                                        {reason && (
                                          <MessageSquare className="w-3 h-3 text-slate-400 mt-0.5" />
                                        )}
                                      </div>
                                    )}
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {isAttending ? (
                                      <span>Vai participar</span>
                                    ) : (
                                      <div className="text-center">
                                        <p className="font-medium">Não vai participar</p>
                                        {reason && (
                                          <p className="text-xs text-slate-400 mt-1">
                                            Motivo: {reason}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    {canEdit && <p className="text-xs mt-1 text-orange-400">(clique para alterar)</p>}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          );
                        })}
                        
                        <TableCell className="text-center">
                          <Badge 
                            variant={totalDays === data.total_days ? 'default' : 'outline'}
                            className={totalDays === data.total_days ? 'bg-green-500' : ''}
                          >
                            {totalDays}/{data.total_days}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            
            {/* Daily Summary */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500 mb-2">Presentes por dia:</p>
              <div className="flex flex-wrap gap-2">
                {data.days.map(day => {
                  const attendeeCount = Object.values(localAttendance).filter(days => days.includes(day)).length;
                  return (
                    <Badge key={day} variant="outline" className="text-xs">
                      {formatDayHeader(day)}: {attendeeCount} pessoa(s)
                    </Badge>
                  );
                })}
              </div>
            </div>
            
            {/* Absence reasons summary */}
            {Object.keys(absenceReasons).length > 0 && !editMode && (
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" />
                  Motivos de ausência:
                </p>
                <div className="space-y-1">
                  {data.members.map(member => {
                    const memberReasons = Object.entries(absenceReasons)
                      .filter(([key]) => key.startsWith(member.user_id))
                      .map(([key, reason]) => ({
                        day: key.split('-').slice(1).join('-'),
                        reason
                      }));
                    
                    if (memberReasons.length === 0) return null;
                    
                    return (
                      <div key={member.user_id} className="text-sm">
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {member.name}:
                        </span>
                        {memberReasons.map(({ day, reason }) => (
                          <span key={day} className="ml-2 text-slate-500">
                            {format(parseISO(day), 'dd/MM', { locale: ptBR })} - {reason}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
      
      <AbsenceReasonModal
        isOpen={reasonModal.isOpen}
        onClose={() => setReasonModal({ isOpen: false, userId: null, day: null, memberName: '' })}
        onConfirm={confirmAbsence}
        memberName={reasonModal.memberName}
        day={reasonModal.day}
      />
      
      <DailyPaymentModal 
        tripId={tripId}
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        attendanceData={data}
        localAttendance={localAttendance}
        onSuccess={() => {
          setShowPaymentModal(false);
          toast.success('Pagamento criado com sucesso!');
        }}
      />
    </Card>
  );
};

// Daily Payment Creation Modal
export const DailyPaymentModal = ({ tripId, isOpen, onClose, attendanceData, localAttendance, onSuccess }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('Pacote de diárias');
  const [totalAmount, setTotalAmount] = useState('');
  const [splitByAttendance, setSplitByAttendance] = useState(true);
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (isOpen && totalAmount && attendanceData) {
      calculatePreview();
    }
  }, [totalAmount, splitByAttendance, attendanceData, localAttendance, isOpen]);

  const calculatePreview = () => {
    if (!attendanceData || !totalAmount) {
      setPreview(null);
      return;
    }

    const amount = parseFloat(totalAmount);
    if (isNaN(amount) || amount <= 0) {
      setPreview(null);
      return;
    }

    const dailyRate = amount / attendanceData.total_days;
    const memberAmounts = [];

    if (splitByAttendance) {
      attendanceData.members.forEach(member => {
        const memberDays = (localAttendance[member.user_id] || []).filter(d => 
          attendanceData.days.includes(d)
        ).length;
        memberAmounts.push({
          name: member.name,
          avatar: member.avatar,
          days: memberDays,
          amount: dailyRate * memberDays
        });
      });
    } else {
      const perMember = amount / attendanceData.members.length;
      attendanceData.members.forEach(member => {
        const memberDays = (localAttendance[member.user_id] || []).filter(d => 
          attendanceData.days.includes(d)
        ).length;
        memberAmounts.push({
          name: member.name,
          avatar: member.avatar,
          days: memberDays,
          amount: perMember
        });
      });
    }

    setPreview({
      dailyRate,
      memberAmounts,
      totalCalculated: memberAmounts.reduce((sum, m) => sum + m.amount, 0)
    });
  };

  const handleSubmit = async () => {
    if (!totalAmount || parseFloat(totalAmount) <= 0) {
      toast.error('Informe um valor total válido');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/trips/${tripId}/payments/daily-package`, {
        title,
        total_amount: parseFloat(totalAmount),
        split_by_attendance: splitByAttendance,
        description: description || null
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pagamento');
    } finally {
      setLoading(false);
    }
  };

  if (!attendanceData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-orange-500" />
            Criar Pagamento de Diárias
          </DialogTitle>
          <DialogDescription>
            Crie um pagamento dividido por diárias da viagem
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="title">Título do pagamento</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Pacote de diárias"
            />
          </div>

          <div>
            <Label htmlFor="amount">Valor total (R$)</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              placeholder="0,00"
            />
            {preview && (
              <p className="text-sm text-slate-500 mt-1">
                = R$ {preview.dailyRate.toFixed(2)} / dia
              </p>
            )}
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                Dividir por presença
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Cada membro paga apenas pelos dias que vai
              </p>
            </div>
            <Switch
              checked={splitByAttendance}
              onCheckedChange={setSplitByAttendance}
            />
          </div>

          <div>
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Hospedagem no sítio"
            />
          </div>

          {preview && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <p className="font-medium text-slate-900 dark:text-white mb-3">
                Prévia da divisão:
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {preview.memberAmounts.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span>{m.avatar}</span>
                      <span className="text-slate-700 dark:text-slate-300">{m.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {m.days} dia(s)
                      </Badge>
                    </div>
                    <span className="font-medium text-slate-900 dark:text-white">
                      R$ {m.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex justify-between">
                <span className="font-medium text-slate-700 dark:text-slate-300">Total:</span>
                <span className="font-bold text-slate-900 dark:text-white">
                  R$ {preview.totalCalculated.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {!splitByAttendance && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-amber-700 dark:text-amber-400">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <p className="text-sm">
                O valor será dividido igualmente entre todos os membros, independente dos dias que cada um vai.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading || !totalAmount}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <DollarSign className="w-4 h-4 mr-2" />
            )}
            Criar Pagamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceGrid;
